import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, ClipboardList, Loader2, Megaphone,
  RefreshCcw, Sparkles, Target, Users, MessageCircle,
} from "lucide-react";
import { api } from "../lib/api";
import AssignTaskModal from "../components/AssignTaskModal";

// ---------------------------------------------------------------------------
// Campaign & Content Playbook (CMO Phase 5, 2026-08).
//
// Structures HOW to execute a CMO task that already exists — objective,
// audience, offer, channels, content plan, creative brief, execution
// checklist, measurement. All context is inherited from the originating
// task; the founder only edits the offer/creative/checklist. Optional
// "Generate Content" is AI-assisted COPY ONLY (never decides context) and
// is always shown with a disclaimer that it's an editable draft.
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = ["DRAFT", "READY", "IN_PROGRESS", "LAUNCHED", "COMPLETED", "CANCELLED"];
const STATUS_STYLE = {
  DRAFT: "bg-slate-100 text-slate-600", READY: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700", LAUNCHED: "bg-emerald-100 text-emerald-700",
  COMPLETED: "bg-emerald-100 text-emerald-700", CANCELLED: "bg-red-100 text-red-700",
};

function Card({ title, icon, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
      <div className="flex items-center gap-2 text-[13px] font-bold text-[#1E3A5F]">{icon}{title}</div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function ContentPlanRow({ item, onGenerate, generating }) {
  const [showVariants, setShowVariants] = useState(false);
  return (
    <div className="rounded-md border border-slate-200 p-3 text-[12.5px]">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <span className="font-semibold text-[#0F1B2D]">{item.content_type}</span>
          <span className="ml-2 text-slate-400">· {item.channel}</span>
        </div>
        <button
          data-testid={`playbook-generate-${item.content_type}`.replace(/\s+/g, "-").toLowerCase()}
          onClick={async () => { setShowVariants(true); await onGenerate(item.content_type, item.channel); }}
          disabled={generating}
          className="inline-flex items-center gap-1.5 rounded-md border border-[#B44554]/30 bg-[#FFF5F6] px-3 py-2 text-[12px] font-semibold text-[#B44554] hover:bg-[#FFE7E9] disabled:opacity-50">
          {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {generating ? "Generating…" : "Generate Content"}
        </button>
      </div>
      <p className="mt-1 text-slate-600">{item.suggested_topic}</p>
      <p className="mt-1 text-[11px] text-slate-400">Purpose: {item.purpose} · Timing: {item.timing}</p>
      {showVariants && item.generated?.length > 0 && (
        <div className="mt-2 space-y-1.5 rounded-md bg-slate-50 p-2">
          <p className="text-[10.5px] font-semibold uppercase tracking-wide text-amber-600">AI-generated draft — review before publishing.</p>
          {item.generated.map((v, i) => (
            <p key={i} className="rounded border border-slate-200 bg-white p-2 text-[12px] text-slate-700">{v}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CampaignPlaybookPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ruleId = params.get("rule_id");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playbook, setPlaybook] = useState(null);
  const [offerDraft, setOfferDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(null);
  const [genContent, setGenContent] = useState({}); // { "type::channel": [variants] }
  const [assignItem, setAssignItem] = useState(null);

  const load = async () => {
    if (!ruleId) { setError("Missing rule_id."); setLoading(false); return; }
    setLoading(true);
    const res = await api.cmoPlaybookBuild(ruleId);
    setLoading(false);
    if (!res.ok || res.data?.error) {
      setError(res.data?.error === "task_not_found_or_no_longer_active"
        ? "This task is no longer active (it may have been completed or dismissed)."
        : res.data?.error === "no_playbook_for_this_task_type"
        ? "This task does not require a campaign playbook yet."
        : res.error || "Unable to load this playbook. Showing the last available data if any.");
      return;
    }
    setPlaybook(res.data);
    setOfferDraft(res.data?.sections?.offer?.founder_offer || "");
  };

  useEffect(() => { load(); }, [ruleId]); // eslint-disable-line

  const saveOffer = async () => {
    if (!playbook?.id) return;
    setSaving(true);
    const sections = { ...playbook.sections, offer: { ...playbook.sections.offer, founder_offer: offerDraft } };
    const res = await api.cmoPlaybookPatch(playbook.id, { sections });
    setSaving(false);
    if (res.ok) setPlaybook((p) => ({ ...p, sections }));
  };

  const toggleChecklist = async (idx) => {
    if (!playbook?.id) return;
    const checklist = playbook.sections.execution_checklist.map((c, i) => i === idx ? { ...c, done: !c.done } : c);
    const sections = { ...playbook.sections, execution_checklist: checklist };
    setPlaybook((p) => ({ ...p, sections })); // optimistic
    await api.cmoPlaybookPatch(playbook.id, { sections });
  };

  const setStatus = async (status) => {
    if (!playbook?.id) return;
    const res = await api.cmoPlaybookPatch(playbook.id, { status });
    if (res.ok) setPlaybook((p) => ({ ...p, status }));
  };

  const generateContent = async (contentType, channel) => {
    if (!playbook?.id) return;
    const key = `${contentType}::${channel}`;
    setGeneratingKey(key);
    const res = await api.cmoPlaybookGenerateContent(playbook.id, contentType, channel, 3);
    setGeneratingKey(null);
    if (res.ok && Array.isArray(res.data?.variants)) {
      setGenContent((g) => ({ ...g, [key]: res.data.variants }));
    }
  };

  if (loading) {
    return (
      <div data-testid="playbook-loading" className="p-8 text-center text-[13px] text-slate-500">
        <Loader2 className="mx-auto mb-2 animate-spin" size={20} /> Loading campaign playbook…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 desktop:p-8">
        <button onClick={() => navigate("/cmo")} className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-slate-700">
          <ArrowLeft size={14} /> Back to CMO
        </button>
        <div data-testid="playbook-error" className="mt-4 rounded-xl border border-dashed border-red-200 bg-red-50/50 p-6 text-center text-[13px] text-red-700">
          <p>{error}</p>
          <button data-testid="playbook-retry" onClick={load}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-2 text-[12px] font-semibold text-red-700 hover:bg-red-50">
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (!playbook) return null;

  const s = playbook.sections || {};
  const ctx = s.campaign_context || {};

  return (
    <div data-testid="campaign-playbook-page" className="mx-auto max-w-4xl space-y-4 p-4 tablet:p-6 desktop:p-8">
      <button onClick={() => navigate("/cmo")} className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-slate-700">
        <ArrowLeft size={14} /> Back to CMO
      </button>

      {!playbook.task_active && (
        <div data-testid="playbook-inactive-banner" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
          This task has been dismissed or marked done — this playbook is now historical (still viewable/editable).
        </div>
      )}

      <div className="rounded-xl border border-[#FFCED1] bg-white p-5 shadow-card">
        <div className="flex items-center gap-2 flex-wrap">
          <Megaphone size={16} className="text-[#B44554]" />
          <h1 data-testid="playbook-title" className="text-[16px] font-bold text-[#0F1B2D]">{playbook.context?.task_title || "Campaign Playbook"}</h1>
          <span className={`ml-auto rounded-full px-2 py-0.5 text-[10.5px] font-bold ${STATUS_STYLE[playbook.status] || STATUS_STYLE.DRAFT}`} data-testid="playbook-status">
            {playbook.status}
          </span>
        </div>
        <p className="mt-1 text-[11.5px] font-semibold uppercase tracking-wide text-[#B44554]">
          {(playbook.playbook_type || "").replace(/_/g, " ")}
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 text-[12px] text-slate-600 tablet:grid-cols-4">
          <div><span className="font-semibold text-slate-400">Country: </span>{(ctx.country || []).join(", ") || "—"}</div>
          <div><span className="font-semibold text-slate-400">Product: </span>{ctx.product || "—"}</div>
          <div><span className="font-semibold text-slate-400">Priority: </span>{ctx.priority ?? "—"}</div>
          <div><span className="font-semibold text-slate-400">Timing: </span>{ctx.timing || "—"}</div>
        </div>
        {playbook.task_active && (
          <button
            data-testid="playbook-assign-task"
            onClick={() => setAssignItem({
              rule_id: ruleId, title: playbook.context?.task_title || "Campaign Playbook",
              priority: playbook.live_task?.priority ?? null,
              detail: s.objective?.summary,
              due_timeline: playbook.live_task?.timing_window || playbook.live_task?.due_date || null,
            })}
            className="mt-3 inline-flex min-h-[38px] items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-[12px] font-semibold text-emerald-700 hover:bg-emerald-100">
            <MessageCircle size={12} /> Assign Task
          </button>
        )}
      </div>

      <Card title="Objective" icon={<Target size={14} className="text-[#B44554]" />} testid="playbook-objective">
        <p className="text-[13px] text-slate-700">{s.objective?.summary}</p>
        <p className="mt-1 text-[11.5px] text-slate-500">{s.objective?.source_rationale}</p>
      </Card>

      <Card title="Target Audience" icon={<Users size={14} className="text-[#B44554]" />} testid="playbook-audience">
        <p className="text-[13px] text-slate-700">{s.target_audience}</p>
      </Card>

      <Card title="Offer / Value Proposition" icon={<ClipboardList size={14} className="text-[#B44554]" />} testid="playbook-offer">
        <ul className="list-disc pl-4 text-[12.5px] text-slate-600">
          {(s.offer?.suggested_structures || []).map((sug, i) => <li key={i}>{sug}</li>)}
        </ul>
        <label className="mt-3 block text-[11.5px] font-semibold text-slate-500">Your actual offer (editable — never invented by C-ORE):</label>
        <textarea data-testid="playbook-offer-input" value={offerDraft} onChange={(e) => setOfferDraft(e.target.value)}
          placeholder="e.g. 15% off + free shipping, valid until..."
          className="mt-1 w-full rounded-md border border-slate-200 p-2 text-[12.5px] focus:border-[#B44554] focus:outline-none" rows={2} />
        <button data-testid="playbook-save-offer" onClick={saveOffer} disabled={saving}
          className="mt-2 inline-flex min-h-[38px] items-center gap-1.5 rounded-md bg-[#B44554] px-4 py-2 text-[12px] font-semibold text-white hover:bg-[#9c3a48] disabled:opacity-50">
          {saving ? "Saving…" : "Save"}
        </button>
      </Card>

      <Card title="Channel Strategy" icon={<Megaphone size={14} className="text-[#B44554]" />} testid="playbook-channels">
        <div className="flex flex-wrap gap-2">
          {(s.channels || []).map((c, i) => (
            <span key={i} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11.5px] text-slate-600">
              {c.channel} <span className="ml-1 text-[10px] font-semibold uppercase text-amber-600">Recommended</span>
            </span>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-slate-400">C-ORE recommends channels based on your business model — execution happens outside C-ORE today.</p>
      </Card>

      <Card title="Content Plan" icon={<Sparkles size={14} className="text-[#B44554]" />} testid="playbook-content-plan">
        <div className="space-y-2">
          {(s.content_plan || []).map((item, i) => {
            const key = `${item.content_type}::${item.channel}`;
            return (
              <ContentPlanRow key={i} item={{ ...item, generated: genContent[key] }}
                onGenerate={generateContent} generating={generatingKey === key} />
            );
          })}
        </div>
      </Card>

      <Card title="Creative Brief" icon={<Target size={14} className="text-[#B44554]" />} testid="playbook-creative-brief">
        <dl className="space-y-1.5 text-[12.5px] text-slate-700">
          <div><dt className="inline font-semibold text-slate-500">Objective: </dt><dd className="inline">{s.creative_brief?.creative_objective}</dd></div>
          <div><dt className="inline font-semibold text-slate-500">Visual direction: </dt><dd className="inline">{s.creative_brief?.visual_direction}</dd></div>
          <div><dt className="inline font-semibold text-slate-500">Key message: </dt><dd className="inline">{s.creative_brief?.key_message}</dd></div>
          <div><dt className="inline font-semibold text-slate-500">Product focus: </dt><dd className="inline">{s.creative_brief?.product_focus}</dd></div>
          <div><dt className="inline font-semibold text-slate-500">CTA: </dt><dd className="inline">{s.creative_brief?.cta}</dd></div>
          <div><dt className="inline font-semibold text-slate-500">Required assets: </dt><dd className="inline">{(s.creative_brief?.required_assets || []).join(", ")}</dd></div>
        </dl>
      </Card>

      <Card title="Execution Checklist" icon={<CheckCircle2 size={14} className="text-[#B44554]" />} testid="playbook-checklist">
        <p className="mb-2 text-[11.5px] font-semibold text-slate-500" data-testid="playbook-checklist-progress">
          Execution Progress: {(s.execution_checklist || []).filter((c) => c.done).length} / {(s.execution_checklist || []).length} complete
        </p>
        <div className="grid grid-cols-1 gap-1.5 tablet:grid-cols-2">
          {(s.execution_checklist || []).map((item, i) => (
            <label key={i} data-testid={`playbook-checklist-${i}`} className="flex items-center gap-2 text-[12.5px] text-slate-700">
              <input type="checkbox" checked={!!item.done} onChange={() => toggleChecklist(i)} className="h-3.5 w-3.5 accent-[#B44554]" />
              <span className={item.done ? "text-slate-400 line-through" : ""}>{item.label}</span>
            </label>
          ))}
        </div>
      </Card>

      <Card title="Measurement" icon={<RefreshCcw size={14} className="text-[#B44554]" />} testid="playbook-measurement">
        <div className="flex flex-wrap gap-2">
          {(s.measurement || []).map((k, i) => (
            <span key={i} className="rounded-full bg-slate-100 px-2.5 py-1 text-[11.5px] text-slate-500" title={k.note}>{k.kpi}</span>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Measurement requires connected campaign/ads data — not yet available.</p>
      </Card>

      <div className="flex flex-wrap items-center gap-2 pb-6">
        {STATUS_OPTIONS.map((st) => (
          <button key={st} data-testid={`playbook-status-${st}`} onClick={() => setStatus(st)}
            className={`min-h-[38px] rounded-md border px-3 py-2 text-[11.5px] font-semibold ${playbook.status === st ? "border-[#B44554] bg-[#FFF5F6] text-[#B44554]" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
            {st.replace("_", " ")}
          </button>
        ))}
      </div>
      <AssignTaskModal item={assignItem} onClose={() => setAssignItem(null)} />
    </div>
  );
}
